import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { apiRequest } from '../api/client.js';

type IntegrationStatus = {
  readonly enabled: boolean | null;
  readonly checkEnabled: () => Promise<boolean>;
};
const Context = createContext<IntegrationStatus | null>(null);

export const SystemStatusProvider = ({ children }: { readonly children: ReactNode }) => {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const generation = useRef(0);
  const checkEnabled = useCallback(async () => {
    const request = ++generation.current;
    setEnabled(null);
    try {
      const result = await apiRequest<{ data?: { metaIntegrationEnabled?: unknown } }>('/api/v1/system/status');
      const confirmed = result?.data?.metaIntegrationEnabled;
      if (request !== generation.current) return false;
      setEnabled(typeof confirmed === 'boolean' ? confirmed : null);
      return confirmed === true;
    } catch {
      if (request === generation.current) setEnabled(null);
      return false;
    }
  }, []);
  useEffect(() => {
    void checkEnabled();
    return () => { generation.current += 1; };
  }, [checkEnabled]);
  const value = useMemo(() => ({ enabled, checkEnabled }), [enabled, checkEnabled]);
  return createElement(Context.Provider, { value }, children);
};

export const useMetaIntegration = (): IntegrationStatus => {
  const context = useContext(Context);
  if (context === null) throw new Error('useMetaIntegration requires SystemStatusProvider');
  return context;
};
